/**
 * Browser-only HTML sanitizer for rich-text (DOMParser).
 * Strip dangerous tags/attributes; keep safe href on links.
 */
const ALLOWED_TAGS = new Set([
    'b',
    'strong',
    'i',
    'em',
    'u',
    's',
    'p',
    'br',
    'div',
    'span',
    'ul',
    'ol',
    'li',
    'a',
]);
function safeHref(raw) {
    const href = String(raw || '').trim();
    if (!href)
        return null;
    if (href.startsWith('/') || href.startsWith('#'))
        return href;
    const lower = href.toLowerCase();
    if (lower.startsWith('http://') ||
        lower.startsWith('https://') ||
        lower.startsWith('mailto:') ||
        lower.startsWith('tel:')) {
        return href;
    }
    return null;
}
export function sanitizeRichTextHtml(input) {
    const html = String(input || '');
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const walk = (node) => {
        if (node.nodeType === Node.COMMENT_NODE) {
            node.parentNode?.removeChild(node);
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE)
            return;
        const el = node;
        const tag = el.tagName.toLowerCase();
        if (['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta'].includes(tag)) {
            el.remove();
            return;
        }
        if (!ALLOWED_TAGS.has(tag)) {
            const parent = el.parentNode;
            if (!parent)
                return;
            while (el.firstChild)
                parent.insertBefore(el.firstChild, el);
            parent.removeChild(el);
            return;
        }
        const attrs = Array.from(el.attributes);
        for (const a of attrs) {
            const name = a.name.toLowerCase();
            if (tag === 'a' && name === 'href')
                continue;
            el.removeAttribute(a.name);
        }
        for (const a of Array.from(el.attributes)) {
            if (a.name.toLowerCase().startsWith('on'))
                el.removeAttribute(a.name);
            if (a.name.toLowerCase() === 'style')
                el.removeAttribute(a.name);
        }
        if (tag === 'a') {
            const href = safeHref(el.getAttribute('href') || '');
            if (href) {
                el.setAttribute('href', href);
                el.setAttribute('rel', 'noopener noreferrer nofollow');
            }
            else {
                el.removeAttribute('href');
            }
        }
        const children = Array.from(el.childNodes);
        children.forEach(walk);
    };
    Array.from(doc.body.childNodes).forEach(walk);
    return doc.body.innerHTML;
}
export function htmlToText(input) {
    const html = String(input || '');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').trim();
}
